import React from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import PageSeo from "../components/site/PageSeo";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import AuthenticMomentumFAQ from "../components/site/AuthenticMomentumFAQ";
import StructuredMomentumMethod from "../components/site/StructuredMomentumMethod";
import { SITE_URL, ORGANIZATION, PERSON } from "../data/entityGraph";

/**
 * /framework
 *
 * The public framework page. Leads with the five-pillar movement
 * (Identity > Clarity > Structure > Execution > Proof) which is
 * the visitor-facing through-line of Authentic Momentum.
 *
 * Signal / Frame / Action are preserved beneath the five pillars
 * as the supporting philosophical layer that lives inside the
 * book. They are not erased; they are reframed as the inner
 * philosophy that drives the outer movement.
 */
const PILLARS = [
  {
    id: "identity",
    label: "Identity",
    line: "Who are you becoming?",
    body: "Movement starts with honesty about who is making the move. Without identity, every strategy borrows energy from a version of you that is no longer current.",
  },
  {
    id: "clarity",
    label: "Clarity",
    line: "What is actually happening?",
    body: "We slow down enough to see the real shape of your friction, your gift, and your current season. Clarity is the difference between guessing and choosing.",
  },
  {
    id: "structure",
    label: "Structure",
    line: "What needs to be organized?",
    body: "Insight becomes useful when it is translated into a frame: an offer, a message, a plan, a system, a pathway, a decision.",
  },
  {
    id: "execution",
    label: "Execution",
    line: "What action must happen next?",
    body: "Movement requires a single honest next move. Not twelve. Not someday. One decision, taken from clarity, that sets the next thing into motion.",
  },
  {
    id: "proof",
    label: "Proof",
    line: "What result, asset, or visible movement confirms progress?",
    body: "Momentum becomes real when it leaves a trace: a launch, an asset, a reflection, a system, a relationship activated, a result documented.",
  },
];

const PHILOSOPHY = [
  {
    id: "signal",
    label: "Signal",
    body: "The quiet truth beneath the strategy. When you act from signal, energy compounds. When you act from noise, you spend it.",
  },
  {
    id: "frame",
    label: "Frame",
    body: "The lens you cannot see from the inside. A new frame is worth more than a new tactic.",
  },
  {
    id: "action",
    label: "Action",
    body: "The single next move that is honest for where you are. One decision, taken from clarity.",
  },
];

const FrameworkPage = () => {
  const definedTermLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": `${SITE_URL}/framework#term`,
    name: "Authentic Momentum Framework",
    description:
      "A five-pillar framework for turning scattered potential into structured momentum. Identity, Clarity, Structure, Execution, Proof. Created by Cyrus Prescod.",
    url: `${SITE_URL}/framework`,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Authentic Momentum",
      url: SITE_URL,
    },
    creator: { ...PERSON },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Framework", item: `${SITE_URL}/framework` },
    ],
  };

  return (
    <>
      <PageSeo
        title="The Authentic Momentum Framework | Identity, Clarity, Structure, Execution, Proof"
        description="The Authentic Momentum framework in five pillars. Identity, Clarity, Structure, Execution, Proof. A practical movement by Cyrus Prescod for turning scattered potential into structured momentum."
        canonical={`${SITE_URL}/framework`}
        jsonLd={[
          definedTermLd,
          breadcrumbLd,
          { "@context": "https://schema.org", ...ORGANIZATION },
        ]}
      />
      <Navigation />
      <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />

        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              The Framework
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-themed leading-tight tracking-tight font-light">
              Turn scattered potential into{" "}
              <span className="italic gold-gradient-text">structured momentum.</span>
            </h1>
            <p className="mt-7 text-white/85 leading-relaxed max-w-2xl mx-auto text-[17px]">
              The Authentic Momentum framework in five pillars. Each pillar earns the next. Identity becomes clarity. Clarity becomes structure. Structure becomes execution. Execution becomes proof.
            </p>
          </Reveal>
        </section>

        <section
          data-testid="framework-pillars"
          className="relative max-w-5xl mx-auto px-6 md:px-10 mt-20 space-y-5 md:space-y-6"
        >
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={100 + i * 80}>
              <article
                data-testid={`framework-pillar-${p.id}`}
                className="bg-white/5 border-t border-gold/20 p-8 md:p-10 rounded-lg border border-gold/10"
              >
                <div className="flex items-baseline gap-4 md:gap-6">
                  <span className="font-serif text-3xl md:text-4xl text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">
                      Pillar {String(i + 1).padStart(2, "0")}
                    </p>
                    <h2 className="font-serif text-2xl md:text-3xl text-themed font-light leading-snug mt-1">
                      {p.label}
                    </h2>
                    <p className="mt-2 font-serif italic text-themed/90 text-[16px] md:text-[17px]">
                      {p.line}
                    </p>
                  </div>
                </div>
                <p className="mt-6 max-w-3xl text-white/85 text-[16px] md:text-[17px] leading-relaxed">
                  {p.body}
                </p>
              </article>
            </Reveal>
          ))}
        </section>

        {/* Canonical Structured Momentum Method - moved off homepage. The
            homepage now carries a single-line strip linking to this
            anchor; here the full six-step method is documented in
            full so the framework page becomes the canonical home of
            the inner method. */}
        <StructuredMomentumMethod />

        {/* Supporting philosophical layer - Signal / Frame / Action. Lives
            inside the book and inside the inner reading of the work. Not
            removed from the canon; reframed as the philosophy underneath
            the public five-pillar movement. */}
        <section
          data-testid="framework-inner-philosophy"
          className="relative max-w-5xl mx-auto px-6 md:px-10 mt-24"
        >
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
              The Inner Philosophy
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-themed leading-tight tracking-tight font-light text-center max-w-2xl mx-auto">
              Inside the book lives a quieter{" "}
              <span className="italic gold-gradient-text">philosophy.</span>
            </h2>
            <p className="mt-7 max-w-2xl mx-auto text-center text-white/85 leading-relaxed">
              The five pillars describe the movement. Signal, Frame, and Action describe the inner posture that makes the movement possible. Both layers are the work.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.id} delay={120 + i * 80}>
                <article
                  data-testid={`framework-philosophy-${p.id}`}
                  className="bg-white/[0.03] border-t border-gold/10 p-7 md:p-8 rounded-lg border border-gold/[0.08] h-full"
                >
                  <p className="text-[10px] tracking-[0.4em] uppercase text-gold/65">
                    Inner Posture
                  </p>
                  <h3 className="mt-2 font-serif text-2xl text-themed/95 font-light">
                    {p.label}
                  </h3>
                  <p className="mt-4 text-white/80 text-[15.5px] leading-relaxed">
                    {p.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24 text-center">
          <Reveal>
            <p className="font-serif italic text-themed text-xl md:text-2xl leading-snug">
              "When you stop moving from pressure and start moving from truth, momentum becomes natural."
            </p>
            <p className="mt-3 text-[10px] tracking-[0.4em] uppercase text-gold/80">
              Cyrus Prescod
            </p>
          </Reveal>
        </section>

        <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-20">
          <Reveal>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="/cyrus-prescod"
                data-testid="framework-cta-cyrus"
                className="btn-ghost-gold inline-flex"
              >
                Who Created This
              </a>
              <a
                href="/book"
                data-testid="framework-cta-book"
                className="btn-ghost-gold inline-flex"
              >
                Read the Book
              </a>
              <a
                href="/start-here"
                data-testid="framework-cta-start-here"
                className="btn-gold inline-flex"
              >
                Where to Begin
              </a>
            </div>
          </Reveal>
        </section>

        <AuthenticMomentumFAQ />
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default FrameworkPage;
